// ═══════════════════════════════════════════════════════════
// LicenseExpiredBanner — Full-width alert when license is expired
// ═══════════════════════════════════════════════════════════
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldAlert, ChevronRight } from "lucide-react";
import { useLicenseStatus } from "@/hooks/use-license";
import { useAuthStore } from "@/stores/auth.store";
import { cn } from "@/lib/utils";

export function LicenseExpiredBanner() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const { data: license } = useLicenseStatus();

  const status = license?.status;
  if (!status) return null;

  const isExpired = status === "EXPIRED" || status === "CANCELLED";
  const isGrace = status === "GRACE_PERIOD";

  if (!isExpired && !isGrace) return null;

  const variant = isExpired ? "expired" : "grace";

  return (
    <button
      type="button"
      onClick={() => {
        navigate("/settings?tab=license");
      }}
      className={cn(
        "w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-opacity hover:opacity-90",
        variant === "expired"
          ? "bg-destructive text-destructive-foreground"
          : "bg-amber-500 text-white dark:bg-amber-600",
      )}
    >
      <ShieldAlert className="size-4 shrink-0" />
      <span className="flex-1 text-left">
        {isExpired
          ? isAdmin
            ? t("license.expiredBannerAdmin")
            : t("license.expiredBannerUser")
          : isAdmin
            ? t("license.graceBannerAdmin")
            : t("license.graceBannerUser")}
      </span>
      {isAdmin && (
        <span className="flex items-center gap-0.5 text-xs opacity-80 shrink-0">
          {t("license.manageBilling")}
          <ChevronRight className="size-3.5" />
        </span>
      )}
    </button>
  );
}
